import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { FaUsers, FaEdit } from 'react-icons/fa';
import MultiSelectDropdown from '../layout/MultiSelectDropdown1';
import {
  fetchRecordViewConfigRows,
  fetchObjectMetaBatch,
  configRowsToMapByObject,
  saveRecordViewConfig,
  fetchLayoutConfigRows,
  saveLayoutConfig,
  fetchLayoutsForObjectNames,
} from './recordViewLayoutRoleApi';
import { clearPermissionCache } from '../../utils/permissionCache';

function formatRvLabels(ids, meta) {
  const recordviews = meta?.recordviews || [];
  const labels = (ids || [])
    .map((id) => {
      const r = recordviews.find((x) => String(x.id) === String(id));
      return r ? r.view_label || r.name || id : id;
    })
    .filter(Boolean);
  return labels.length ? labels.join(', ') : '—';
}

function layoutRowId(lo) {
  return lo?.id ?? lo?.layout_id;
}

function formatAssignedLayout(ids, layoutsWrap) {
  const layouts = layoutsWrap?.layouts || [];
  const id = (ids || [])[0];
  if (!id) return '—';
  const lo = layouts.find((x) => String(layoutRowId(x)) === String(id));
  return lo ? lo.layout_label || lo.layout_name || id : id;
}

/** Active, non-deleted record views the role can be assigned (same filter as picklist). */
function assignableRecordViewCount(meta) {
  const m = meta || { recordviews: [] };
  return (m.recordviews || []).filter((rv) => !rv.is_deleted && rv.is_active !== false).length;
}

/**
 * Record views + assigned layout per object: people icon opens record view modal; pencil opens layout modal.
 */
const RecordViewLayoutTableSection = ({ roleId, permissions, isAdminEditingOwnRole, onNotify }) => {
  const onNotifyRef = useRef(onNotify);
  onNotifyRef.current = onNotify;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [displayRows, setDisplayRows] = useState([]);
  const [rvIdsByObject, setRvIdsByObject] = useState({});
  const [baselineRvIds, setBaselineRvIds] = useState({});
  const [layoutIdsByObject, setLayoutIdsByObject] = useState({});
  const [metaByObject, setMetaByObject] = useState({});
  const [layoutsMetaByObject, setLayoutsMetaByObject] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  const [modalRow, setModalRow] = useState(null);
  const [modalRvIds, setModalRvIds] = useState([]);

  const [layoutModalRow, setLayoutModalRow] = useState(null);
  const [layoutModalLayoutId, setLayoutModalLayoutId] = useState('');

  const objectNamesKey = useMemo(
    () =>
      (permissions || [])
        .map((p) => p.object_name)
        .filter(Boolean)
        .join('\0'),
    [permissions]
  );

  const reloadFromServer = useCallback(async () => {
    if (!roleId || !permissions?.length) {
      setDisplayRows([]);
      setRvIdsByObject({});
      setBaselineRvIds({});
      setLayoutIdsByObject({});
      setMetaByObject({});
      setLayoutsMetaByObject({});
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const objectNames = [...new Set(permissions.map((p) => p.object_name).filter(Boolean))];
      const [configRows, layoutRows, meta, layoutsMeta] = await Promise.all([
        fetchRecordViewConfigRows(roleId),
        fetchLayoutConfigRows(roleId),
        fetchObjectMetaBatch(objectNames),
        fetchLayoutsForObjectNames(objectNames),
      ]);
      const cfgMap = configRowsToMapByObject(configRows);
      const layoutCfgMap = configRowsToMapByObject(layoutRows);
      const nextRv = {};
      const nextBaseRv = {};
      const nextLayout = {};
      const rows = permissions.map((p) => {
        const on = p.object_name;
        const row = cfgMap[on];
        const ids = (row?.recordview_ids || []).map(String);
        nextRv[on] = [...ids];
        nextBaseRv[on] = [...ids];
        const lRow = layoutCfgMap[on];
        const lids = (lRow?.layout_ids || []).map(String);
        nextLayout[on] = lids.length ? [lids[0]] : [];
        const m = meta[on] || { recordviews: [], error: null };
        return {
          key: p.id || on,
          objectName: on,
          objectLabel: p.object_display_name || on,
          isStandard: p.is_standard === true,
          metaError: m.error,
        };
      });
      setDisplayRows(rows);
      setRvIdsByObject(nextRv);
      setBaselineRvIds(nextBaseRv);
      setLayoutIdsByObject(nextLayout);
      setMetaByObject(meta);
      setLayoutsMetaByObject(layoutsMeta);
    } catch (e) {
      setDisplayRows([]);
      onNotifyRef.current?.('danger', e.response?.data?.message || e.message || 'Failed to load record views');
    } finally {
      setLoading(false);
    }
  }, [roleId, permissions, objectNamesKey]);

  useEffect(() => {
    reloadFromServer();
  }, [reloadFromServer]);

  const openAssignModal = (row) => {
    const on = row.objectName;
    setModalRow(row);
    setModalRvIds([...(rvIdsByObject[on] || [])].map(String));
  };

  const openLayoutModal = (row) => {
    const on = row.objectName;
    setLayoutModalRow(row);
    const cur = layoutIdsByObject[on] || [];
    setLayoutModalLayoutId(cur[0] ? String(cur[0]) : '');
  };

  const closeModal = () => {
    if (saving) return;
    setModalRow(null);
    setModalRvIds([]);
  };

  const closeLayoutModal = () => {
    if (saving) return;
    setLayoutModalRow(null);
    setLayoutModalLayoutId('');
  };

  const modalDirty = useMemo(() => {
    if (!modalRow) return false;
    const on = modalRow.objectName;
    const norm = (a) => [...(a || [])].map(String).sort().join('|');
    return norm(modalRvIds) !== norm(rvIdsByObject[on]);
  }, [modalRow, modalRvIds, rvIdsByObject]);

  const layoutModalDirty = useMemo(() => {
    if (!layoutModalRow) return false;
    const on = layoutModalRow.objectName;
    const cur = (layoutIdsByObject[on] || [])[0] || '';
    return String(layoutModalLayoutId || '').trim() !== String(cur || '').trim();
  }, [layoutModalRow, layoutModalLayoutId, layoutIdsByObject]);

  const handleModalSave = async () => {
    if (!modalRow || !modalDirty || !permissions?.length) {
      closeModal();
      return;
    }
    const on = modalRow.objectName;
    const nextRvMap = {
      ...rvIdsByObject,
      [on]: [...modalRvIds.map(String)],
    };
    setSaving(true);
    try {
      const items = permissions.map((p) => {
        const objectName = p.object_name;
        return {
          object_name: objectName,
          recordview_ids: nextRvMap[objectName] || [],
        };
      });
      const res = await saveRecordViewConfig(roleId, items);
      if (res?.success) {
        clearPermissionCache();
        setRvIdsByObject(nextRvMap);
        setBaselineRvIds((prev) => ({
          ...prev,
          [on]: [...modalRvIds.map(String)],
        }));
        onNotifyRef.current?.('success', res.message || 'Record views saved.');
        closeModal();
      } else {
        onNotifyRef.current?.('danger', res?.message || 'Save failed');
      }
    } catch (e) {
      onNotifyRef.current?.('danger', e.response?.data?.message || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleLayoutModalSave = async () => {
    if (!layoutModalRow || !layoutModalDirty || !permissions?.length) {
      closeLayoutModal();
      return;
    }
    const on = layoutModalRow.objectName;
    const chosen = String(layoutModalLayoutId || '').trim();
    const nextLayoutMap = { ...layoutIdsByObject };
    nextLayoutMap[on] = chosen ? [chosen] : [];

    setSaving(true);
    try {
      const items = permissions.map((p) => {
        const objectName = p.object_name;
        const ids = nextLayoutMap[objectName] || [];
        return {
          object_name: objectName,
          layout_ids: ids.length ? [ids[0]] : [],
        };
      });
      const res = await saveLayoutConfig(roleId, items);
      if (res?.success) {
        clearPermissionCache();
        setLayoutIdsByObject(nextLayoutMap);
        try {
          const rvRows = await fetchRecordViewConfigRows(roleId);
          const cfgMap = configRowsToMapByObject(rvRows);
          const nextRv = {};
          const nextBaseRv = {};
          permissions.forEach((p) => {
            const objectName = p.object_name;
            const row = cfgMap[objectName];
            const ids = (row?.recordview_ids || []).map(String);
            nextRv[objectName] = [...ids];
            nextBaseRv[objectName] = [...ids];
          });
          setRvIdsByObject(nextRv);
          setBaselineRvIds(nextBaseRv);
        } catch {
          /* record view refresh is best-effort; layout save already persisted */
        }
        onNotifyRef.current?.('success', res.message || 'Layout assignment saved.');
        closeLayoutModal();
      } else {
        onNotifyRef.current?.('danger', res?.message || 'Save failed');
      }
    } catch (e) {
      onNotifyRef.current?.('danger', e.response?.data?.message || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  /** Objects with at least one record view to assign — omit others (no empty rows). */
  const eligibleRows = useMemo(
    () =>
      displayRows.filter((r) => assignableRecordViewCount(metaByObject[r.objectName]) > 0),
    [displayRows, metaByObject]
  );

  const filteredRows = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return eligibleRows;
    return eligibleRows.filter(
      (r) =>
        (r.objectLabel || '').toLowerCase().includes(q) ||
        (r.objectName || '').toLowerCase().includes(q)
    );
  }, [eligibleRows, searchTerm]);

  const standardRows = filteredRows.filter((r) => r.isStandard);
  const customRows = filteredRows.filter((r) => !r.isStandard);

  const modalMeta = modalRow ? metaByObject[modalRow.objectName] || { recordviews: [], error: null } : null;
  const modalPicklist =
    modalMeta &&
    (modalMeta.recordviews || [])
      .filter((rv) => !rv.is_deleted && rv.is_active !== false)
      .map((rv) => ({
        key: String(rv.id),
        value: `${rv.view_label || rv.name || rv.id}`,
      }));
  const modalLockedKeys =
    modalRow && isAdminEditingOwnRole ? (baselineRvIds[modalRow.objectName] || []).map(String) : [];

  const layoutModalLayoutsWrap = layoutModalRow
    ? layoutsMetaByObject[layoutModalRow.objectName] || { layouts: [], error: null }
    : null;

  if (!permissions?.length) {
    return null;
  }

  if (!loading && eligibleRows.length === 0) {
    return null;
  }

  const renderBody = (rows) =>
    rows.map((r) => {
      const on = r.objectName;
      const meta = metaByObject[on] || { recordviews: [], error: null };
      const ids = rvIdsByObject[on] || [];
      const summary = formatRvLabels(ids, meta);
      const layoutIds = layoutIdsByObject[on] || [];
      const layoutSummary = formatAssignedLayout(layoutIds, layoutsMetaByObject[on] || { layouts: [] });

      return (
        <tr key={r.key} className="recordview-summary-row">
          <td className="object-cell">
            <div className="object-name object-name-field-perms-row d-flex align-items-center w-100 gap-2">
              <span className="object-name-label text-truncate min-w-0">{r.objectLabel}</span>
            </div>
          </td>
          <td className="recordview-summary-cell">
            <div className="recordview-summary-with-action d-flex align-items-center justify-content-between gap-2 w-100">
              <div className="recordview-summary-text min-w-0">
                <span>{summary}</span>
              </div>
              <Button
                variant="outline-secondary"
                size="sm"
                className="recordview-assign-icon flex-shrink-0"
                title="View assignments — choose record views for this role"
                aria-label={`View assignments for ${r.objectLabel}`}
                onClick={() => openAssignModal(r)}
              >
                <FaUsers aria-hidden="true" />
              </Button>
            </div>
          </td>
          <td className="recordview-assigned-layout-cell">
            <div className="recordview-summary-with-action d-flex align-items-center justify-content-between gap-2 w-100">
              <div className="recordview-summary-text min-w-0">
                <span>{layoutSummary}</span>
              </div>
              <Button
                variant="outline-secondary"
                size="sm"
                className="recordview-assign-icon flex-shrink-0"
                title="Edit assigned layout for this object"
                aria-label={`Edit layout for ${r.objectLabel}`}
                onClick={() => openLayoutModal(r)}
              >
                <FaEdit aria-hidden="true" />
              </Button>
            </div>
          </td>
        </tr>
      );
    });

  return (
    <div className="recordview-layout-subsection">
      <div className="permissions-header recordview-section-header">
        <h3>Record views</h3>
      </div>
      <p className="permissions-tabs-summary recordview-layout-summary">
        Summary per object. Use the <strong>people</strong> icon for record views and the <strong>pencil</strong> icon
        to set the assigned layout (detail/edit).
        {isAdminEditingOwnRole && (
          <>
            {' '}
            For the <strong>Admin</strong> role, record views that were already assigned cannot be removed. Clearing an
            assigned layout may be ignored for Admin until all layout keys are updated in a future release.
          </>
        )}
      </p>
      {loading ? (
        <div className="permissions-tabs-summary">Loading…</div>
      ) : (
        <>
          <div className="recordview-inline-toolbar">
            <i className="fas fa-search text-muted" aria-hidden="true" />
            <input
              type="search"
              className="form-control form-control-sm"
              placeholder="Search objects…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search objects"
            />
          </div>
          <div className="permissions-table-container recordview-layout-matrix">
            <table className="permissions-table">
              <thead>
                <tr>
                  <th className="object-column text-dark">Object name</th>
                  <th className="text-dark recordview-col-header">Record views</th>
                  <th className="text-dark recordview-layout-col-header">Assigned layout</th>
                </tr>
              </thead>
              <tbody>
                {standardRows.length > 0 && (
                  <>
                    <tr className="permission-section-header">
                      <td colSpan="3" className="section-header-cell">
                        Standard Objects
                      </td>
                    </tr>
                    {renderBody(standardRows)}
                  </>
                )}
                {customRows.length > 0 && (
                  <>
                    <tr className="permission-section-header">
                      <td colSpan="3" className="section-header-cell">
                        Custom Objects
                      </td>
                    </tr>
                    {renderBody(customRows)}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Modal show={!!modalRow} onHide={closeModal} centered size="md" backdrop={saving ? 'static' : true}>
        <Modal.Header closeButton={!saving}>
          <Modal.Title className="d-flex align-items-center gap-2">
            <FaUsers className="text-secondary" aria-hidden="true" />
            <span>
              View assignments
              {modalRow ? ` — ${modalRow.objectLabel}` : ''}
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalRow?.metaError && (
            <p className="text-warning small">{modalRow.metaError}</p>
          )}
          {modalMeta && !modalRow?.metaError && (modalPicklist?.length || 0) === 0 && (
            <p className="text-muted small mb-0">No record views are defined for this object.</p>
          )}
          {modalPicklist && modalPicklist.length > 0 && (
            <div className="recordview-modal-select">
              <label className="form-label small text-muted mb-2">Record views for this role</label>
              <MultiSelectDropdown
                picklistValues={modalPicklist}
                selectedValues={modalRvIds.map(String)}
                onChange={(keys) => setModalRvIds(keys)}
                placeholder="Select record views…"
                disabled={saving}
                lockedKeys={modalLockedKeys}
              />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleModalSave}
            disabled={saving || !modalDirty || !modalPicklist?.length}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                Saving…
              </>
            ) : (
              'Save'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!layoutModalRow}
        onHide={closeLayoutModal}
        centered
        size="md"
        backdrop={saving ? 'static' : true}
      >
        <Modal.Header closeButton={!saving}>
          <Modal.Title className="d-flex align-items-center gap-2">
            <FaEdit className="text-secondary" aria-hidden="true" />
            <span>
              Assigned layout
              {layoutModalRow ? ` — ${layoutModalRow.objectLabel}` : ''}
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {layoutModalLayoutsWrap?.error && (
            <p className="text-warning small">{layoutModalLayoutsWrap.error}</p>
          )}
          {layoutModalRow && !layoutModalLayoutsWrap?.error && (
            <>
              <Form.Label className="small text-muted">Layout for this object</Form.Label>
              <Form.Select
                value={layoutModalLayoutId}
                onChange={(e) => setLayoutModalLayoutId(e.target.value)}
                disabled={saving}
                aria-label="Select layout"
              >
                <option value="">— None —</option>
                {(layoutModalLayoutsWrap?.layouts || []).map((lo) => {
                  const id = String(layoutRowId(lo) ?? '').trim();
                  if (!id) return null;
                  const label = lo.layout_label || lo.layout_name || id;
                  return (
                    <option key={id} value={id}>
                      {label}
                    </option>
                  );
                })}
              </Form.Select>
              {isAdminEditingOwnRole && (
                <p className="text-muted small mt-2 mb-0">
                  Admin role: removing a layout assignment may not clear the stored value; choose another layout to
                  replace it if needed.
                </p>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeLayoutModal} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleLayoutModalSave} disabled={saving || !layoutModalDirty}>
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                Saving…
              </>
            ) : (
              'Save'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default RecordViewLayoutTableSection;
